import type { Dock } from '@/types/dock';
import type { SiteMarker } from '@/components/dashboard/droneMapTypes';

/**
 * A dock's effective map position.
 *
 * Docks can be created without their own lat/lng (e.g. the first live dock,
 * DOCK-11), and the AirScort status carries no GPS - `airscort-adapter` never
 * parses lat/lng - so nothing ever backfills it. A null-coord dock therefore
 * rendered no marker and vanished from the COP (issue #39).
 *
 * Fall back to the linked site's coordinates so the dock still shows. Returns
 * null only when neither the dock nor a linked site has a position, in which
 * case there is genuinely nowhere to place it.
 */
export function resolveDockPosition(
  dock: Pick<Dock, 'latitude' | 'longitude' | 'site_id'>,
  sites: readonly Pick<SiteMarker, 'id' | 'latitude' | 'longitude'>[],
): { latitude: number; longitude: number } | null {
  if (dock.latitude !== null && dock.longitude !== null) {
    return { latitude: dock.latitude, longitude: dock.longitude };
  }
  if (dock.site_id) {
    const site = sites.find((s) => s.id === dock.site_id);
    if (site) return { latitude: site.latitude, longitude: site.longitude };
  }
  return null;
}
